import type {
  AltValueOption,
  FormFieldOption,
} from '@risksmart-app/form-configuration/src/types';

import type { JSONObject } from '@/types/types';
import { EMPTY_VALUE } from '@/utils/collectionUtils';
import { isString, isStringArray } from '@/utils/utils';

export const matchToField = (
  data: JSONObject | null,
  path: string,
  AltValueOptions?: AltValueOption[]
): string => {
  const key = data?.[path];

  if (key && isString(key)) {
    return mapKeyToValue(key, AltValueOptions) || EMPTY_VALUE;
  }

  if (key && isStringArray(key)) {
    return mapKeysToValues(key, AltValueOptions).join(',');
  }

  return EMPTY_VALUE;
};

export const matchToArrayField = (
  data: JSONObject | null,
  path: string,
  AltValueOptions?: AltValueOption[]
): string[] => {
  const rawValue = data?.[path];
  const key: string[] = Array.isArray(rawValue)
    ? rawValue.filter(isString)
    : typeof rawValue === 'string' && rawValue.length > 0
      ? [rawValue]
      : [];

  return mapKeysToValues(key, AltValueOptions);
};

const mapKeysToValues = (
  key: string[],
  AltValueOptions?: AltValueOption[]
): string[] => {
  if (!AltValueOptions || AltValueOptions.length === 0) {
    return key;
  }

  return (
    key
      .map((k) => mapKeyToValue(k, AltValueOptions))
      .filter((v) => v !== null) ?? []
  );
};

const mapKeyToValue = (
  key: string | null,
  AltValueOptions?: AltValueOption[]
): string | null => {
  if (!key || !AltValueOptions || AltValueOptions.length === 0) {
    return key;
  }

  const match = AltValueOptions.find((option) => option.AltValue === key);

  return match ? match.Value : key;
};

// Exactly what `crypto.randomUUID()` emits: RFC-4122 v4, lowercase.
const EDITOR_MINTED_OPTION_ID =
  /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/;

// Not `!!altLabel` alone — REST accepts an `AltValueOption` with `AltLabel`
// omitted, so that deletes live columns; not the option shape either — the
// editor mints a UUID per option. Nothing persisted tells the two apart.
export const hasConfiguredAlternateValues = (props: {
  altLabel: string | undefined;
  options: FormFieldOption[] | undefined;
}): boolean => {
  if (props.altLabel?.trim()) {
    return true;
  }

  return (
    props.options?.some((option) => {
      if (option._tag !== 'AltValueOption') {
        return false;
      }
      const code = option.AltValue.trim();

      return code.length > 0 && !EDITOR_MINTED_OPTION_ID.test(code);
    }) ?? false
  );
};

/**
 * If provided with formFieldOptions will return a list of alternative value options depending on the useAlternativeValues flag.
 *
 * @param formFieldOptions - A list of form field options.
 * @param useAlternativeValues - A flag indicating whether to use alternative values.
 * @returns A list of alternative value options.
 */
export const resolveDisplayValues = (props: {
  formFieldOptions: FormFieldOption[] | undefined;
  useAlternativeValues: boolean;
}): AltValueOption[] => {
  // this is a bit confusing because when an alternate value is provided, the human readable value is not persisted against the entity
  // therefore the human readable value is technically the alternate value.
  // This is in contrast to the label and altLabel fields as the altLabel is NOT the human readable field.
  return props.useAlternativeValues
    ? []
    : (props.formFieldOptions?.filter((p) => p._tag === 'AltValueOption') ??
        []);
};
